import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Chip,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import { ArrowBack, ArrowForward, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { useProducts, Product } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import PaymentMethodsLogos from '../components/PaymentMethodsLogos';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { fetchSizeStockForProducts, SizeStockMap } from '../extraApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

interface ProductComment {
  author: string;
  rating: number;
  comment: string;
  date: string;
}

interface ProductExtraInfo {
  description: string;
  composition: string;
  images: string[];
  comments: ProductComment[];
}

const productExtras: Record<number, ProductExtraInfo> = {
  1: {
    description:
      "T-shirt blanc classique en coton doux, coupe droite confortable, idéal pour un usage quotidien.",
    composition: '100% coton biologique',
    images: [
      'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=880&q=80',
      'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=880&q=80',
      'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [
      {
        author: 'Camille',
        rating: 5,
        comment: 'Très bonne qualité, le tissu est épais et agréable.',
        date: '12/09/2024',
      },
      {
        author: 'Lucas',
        rating: 4,
        comment: 'Coupe parfaite, taille un peu grand.',
        date: '03/10/2024',
      },
    ],
  },
  2: {
    description:
      'Jean slim noir stretch, confortable et élégant, adapté aussi bien au quotidien qu’aux sorties.',
    composition: '98% coton, 2% élasthanne',
    images: [
      'https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=crop&w=1026&q=80',
      'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [
      {
        author: 'Sarah',
        rating: 5,
        comment: 'Mon jean préféré, super confortable.',
        date: '22/08/2024',
      },
    ],
  },
  3: {
    description:
      'Veste en cuir véritable, finition premium et doublure intérieure pour un confort maximal.',
    composition: '100% cuir véritable, doublure 100% polyester',
    images: [
      'https://images.unsplash.com/photo-1551028719-00167d1e1eb9?auto=format&fit=crop&w=1170&q=80',
      'https://images.unsplash.com/photo-1506157786151-b8491531f063?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [],
  },
  4: {
    description:
      'Robe d’été légère et fluide avec motif fleuri, parfaite pour les journées ensoleillées.',
    composition: '70% viscose, 30% coton',
    images: [
      'https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?auto=format&fit=crop&w=1169&q=80',
      'https://images.unsplash.com/photo-1475180098004-ca77a66827be?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [],
  },
  5: {
    description:
      'Chaussures de sport polyvalentes, adaptées à la course et aux activités quotidiennes.',
    composition: 'Tige textile, semelle extérieure caoutchouc',
    images: [
      'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1170&q=80',
      'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [],
  },
  6: {
    description:
      'Pull en laine chaud et confortable, col rond, idéal pour l’hiver.',
    composition: '80% laine, 20% polyamide',
    images: [
      'https://images.unsplash.com/photo-1576871337622-98d48d1cf531?auto=format&fit=crop&w=1074&q=80',
      'https://images.unsplash.com/photo-1543076447-215ad9ba6923?auto=format&fit=crop&w=880&q=80',
    ],
    comments: [],
  },
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { items: cartItems, addItem } = useCart();
  const productId = Number(id);
  const { products, settings } = useProducts();

  const product: Product | undefined = useMemo(
    () => products.find((p) => p.id === productId),
    [productId, products]
  );

  const extra = productExtras[productId];

  const productImages: string[] =
    product && product.images && product.images.length > 0
      ? product.images
      : product
      ? [product.image]
      : [];

  const images: string[] =
    productImages.length > 0 ? productImages : extra?.images || [];

  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | undefined>(() =>
    product?.sizes && product.sizes.length > 0 ? product.sizes[0] : undefined
  );
  const [selectedColor, setSelectedColor] = useState<string | undefined>(() =>
    product?.colors && product.colors.length > 0 ? product.colors[0] : undefined
  );
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const [reviews, setReviews] = useState<ProductComment[]>([]);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [sizeStock, setSizeStock] = useState<SizeStockMap | null>(null);

  useEffect(() => {
    if (!product) {
      setReviews([]);
      setReviewsError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setReviewsError(null);
        const res = await fetch(
          `${API_BASE_URL}/products/${product.id}/reviews`
        );

        if (!res.ok) {
          return;
        }

        const data = await res.json();
        if (!Array.isArray(data) || cancelled) {
          return;
        }

        const mapped: ProductComment[] = data.map((r: any) => ({
          author:
            typeof r.author_name === 'string' && r.author_name.trim()
              ? r.author_name.trim()
              : 'Client',
          rating:
            typeof r.rating === 'number'
              ? r.rating
              : parseInt(String(r.rating), 10) || 0,
          comment:
            typeof r.comment === 'string'
              ? r.comment
              : '',
          date: r.created_at
            ? new Date(r.created_at).toLocaleDateString()
            : '',
        }));

        if (!cancelled) {
          setReviews(mapped);
        }
      } catch (e) {
        if (!cancelled) {
          setReviewsError('Erreur lors du chargement des avis.');
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [product]);

  useEffect(() => {
    if (!product) {
      setSizeStock(null);
      return;
    }

    let cancelled = false;

    const loadSizeStock = async () => {
      try {
        const data = await fetchSizeStockForProducts([product.id]);
        if (cancelled) return;

        const fromBackend = data[product.id] || null;
        if (fromBackend && typeof fromBackend === 'object') {
          setSizeStock(fromBackend);
          return;
        }

        if (product.sizeStock) {
          setSizeStock(product.sizeStock);
        } else {
          setSizeStock(null);
        }
      } catch (e) {
        console.error('Erreur lors du chargement du stock par taille pour ce produit', e);
        if (product.sizeStock) {
          setSizeStock(product.sizeStock);
        }
      }
    };

    void loadSizeStock();

    return () => {
      cancelled = true;
    };
  }, [product]);

  const totalStockFromSize = useMemo(() => {
    if (!sizeStock) {
      return null;
    }
    const values = Object.values(sizeStock);
    if (values.length === 0) {
      return null;
    }
    return values.reduce((sum, qty) => {
      const n = typeof qty === 'number' && Number.isFinite(qty) ? qty : 0;
      return sum + (n >= 0 ? n : 0);
    }, 0);
  }, [sizeStock]);

  const selectedSizeStockValue = useMemo(() => {
    if (!selectedSize || !sizeStock) {
      return null;
    }
    const raw = sizeStock[selectedSize];
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      return null;
    }
    return raw;
  }, [selectedSize, sizeStock]);

  if (!product) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h5" gutterBottom>
          Produit introuvable
        </Typography>
        <Button component={RouterLink} to="/products" variant="contained">
          Retour aux produits
        </Button>
      </Container>
    );
  }

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: finalPrice,
      quantity: 1,
      image: product.image,
      size: selectedSize,
      color: selectedColor,
    });
  };

  const handleNextImage = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        handleNextImage();
      } else {
        handlePrevImage();
      }
    }
    setTouchStartX(null);
  };

  const comments = reviews.length > 0 ? reviews : extra?.comments || [];
  const description = product?.description || extra?.description;
  const composition = product?.composition || extra?.composition;
  const hasDiscount = product.discount && product.discount > 0;
  const finalPrice = hasDiscount
    ? product.price * (1 - (product.discount || 0) / 100)
    : product.price;
  const requiresSize = product.sizes && product.sizes.length > 0;
  const requiresColor = product.colors && product.colors.length > 0;

  const hasPerSizeStock = totalStockFromSize != null;

  const existingQuantityForSelection = (() => {
    const itemsForProduct = cartItems.filter((item) => item.id === product.id);

    if (requiresSize && selectedSize) {
      return itemsForProduct
        .filter((item) => item.size === selectedSize)
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    return itemsForProduct.reduce((sum, item) => sum + item.quantity, 0);
  })();

  const remainingQuantityForSelection =
    requiresSize && hasPerSizeStock && selectedSizeStockValue != null
      ? Math.max(0, selectedSizeStockValue - existingQuantityForSelection)
      : null;

  const totalStock =
    totalStockFromSize != null
      ? totalStockFromSize
      : product.stock != null
      ? product.stock
      : null;

  const isSelectedSizeOutOfStock =
    requiresSize &&
    hasPerSizeStock &&
    !!selectedSize &&
    (remainingQuantityForSelection == null || remainingQuantityForSelection <= 0);

  const canAddToCart =
    product.inStock &&
    (!requiresSize || !!selectedSize) &&
    (!requiresColor || !!selectedColor) &&
    !isSelectedSizeOutOfStock;

  const addButtonLabel = !product.inStock
    ? 'Rupture de stock'
    : requiresSize && !selectedSize
    ? 'Sélectionnez une taille'
    : isSelectedSizeOutOfStock
    ? 'Taille indisponible'
    : 'Ajouter au panier';

  const stockLabel =
    totalStock != null
      ? totalStock > 0
        ? `TOTAL : ${totalStock} pièce${totalStock > 1 ? 's' : ''} en stock`
        : 'Rupture de stock'
      : product.inStock
      ? 'En stock'
      : 'Rupture de stock';

  const isLowStock =
    totalStock != null &&
    totalStock > 0 &&
    totalStock <= settings.lowStockThreshold;

  const perSizeLabel =
    selectedSize && hasPerSizeStock
      ? (() => {
          const qty =
            selectedSizeStockValue != null && selectedSizeStockValue >= 0
              ? selectedSizeStockValue
              : 0;
          return `Taille ${selectedSize} : ${qty} pièce${qty > 1 ? 's' : ''} en stock`;
        })()
      : null;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Button
        component={RouterLink}
        to="/products"
        variant="text"
        sx={{ mb: 3 }}
      >
        ← Retour aux produits
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <Box
              component="img"
              src={images[activeIndex]}
              alt={product.name}
              sx={{
                width: '100%',
                height: { xs: 320, md: 420 },
                objectFit: 'cover',
                display: 'block',
                cursor: 'zoom-in',
              }}
              loading="lazy"
              onClick={() => setIsZoomOpen(true)}
            />
            {images.length > 1 && (
              <>
                <Button
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 8,
                    transform: 'translateY(-50%)',
                    minWidth: 'auto',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                >
                  <ArrowBack />
                </Button>
                <Button
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    minWidth: 'auto',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                >
                  <ArrowForward />
                </Button>
              </>
            )}
            {isLowStock && (
              <Chip
                label="Stock faible"
                color="warning"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          {images.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
              {images.map((img, index) => (
                <Box
                  key={img}
                  component="img"
                  src={img}
                  alt={`${product.name} ${index + 1}`}
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border:
                      index === activeIndex
                        ? '2px solid #1976d2'
                        : '2px solid transparent',
                  }}
                  loading="lazy"
                />
              ))}
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {product.name}
          </Typography>
          {product.reviews > 0 && product.rating > 0 && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Rating
                name="product-rating"
                value={product.rating}
                precision={0.5}
                readOnly
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                ({product.rating.toFixed(1)} / 5) – {product.reviews} avis
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="baseline" gap={1} mb={2}>
            {hasDiscount && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                {product.price.toFixed(2)} €
              </Typography>
            )}
            <Typography variant="h5" color="primary" gutterBottom sx={{ mb: 0 }}>
              {finalPrice.toFixed(2)} €
            </Typography>
            {hasDiscount && (
              <Chip
                label={`-${product.discount}%`}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {stockLabel}
          </Typography>
          {perSizeLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {perSizeLabel}
            </Typography>
          )}

          <Box mt={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {description || 'Aucune description disponible.'}
            </Typography>
          </Box>

          <Box mt={2} mb={3}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Composition
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {composition || 'Composition non renseignée.'}
            </Typography>
          </Box>

          {product.sizes && product.sizes.length > 0 && (
            <Box mt={2} mb={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Tailles disponibles
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {product.sizes.map((size) => (
                  <Chip
                    key={size}
                    label={size}
                    clickable
                    color={selectedSize === size ? 'primary' : 'default'}
                    onClick={() => setSelectedSize(size)}
                    variant={selectedSize === size ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {product.colors && product.colors.length > 0 && (
            <Box mt={1} mb={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Couleurs
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {product.colors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: color,
                      border:
                        selectedColor === color
                          ? '2px solid #1976d2'
                          : '1px solid rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                    role="button"
                    aria-label={`Choisir la couleur ${color}`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedColor(color);
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box display="flex" gap={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              onClick={handleAddToCart}
              disabled={!canAddToCart}
            >
              {addButtonLabel}
            </Button>
            <Button
              component={RouterLink}
              to="/cart"
              variant="outlined"
              color="primary"
            >
              Voir le panier
            </Button>
          </Box>
          <PaymentMethodsLogos size={26} />
        </Box>
      </Box>

      <Box mt={6}>
        <Typography variant="h5" gutterBottom>
          Avis des clients
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {reviewsError && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {reviewsError}
          </Typography>
        )}
        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun commentaire pour le moment.
          </Typography>
        ) : (
          <List>
            {comments.map((c, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <Avatar sx={{ mr: 2 }}>
                    {c.author.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2">{c.author}</Typography>
                        <Rating
                          value={c.rating}
                          readOnly
                          size="small"
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {c.date}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {c.comment}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < comments.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {isZoomOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
          }}
          onClick={() => setIsZoomOpen(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 900,
              maxHeight: '100vh',
              px: 2,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Box
              component="img"
              src={images[activeIndex]}
              alt={product.name}
              sx={{
                width: '100%',
                maxHeight: { xs: '80vh', md: '90vh' },
                objectFit: 'contain',
                display: 'block',
              }}
            />
            {images.length > 1 && (
              <>
                <Button
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 16,
                    transform: 'translateY(-50%)',
                    minWidth: 'auto',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                >
                  <ArrowBack />
                </Button>
                <Button
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 16,
                    transform: 'translateY(-50%)',
                    minWidth: 'auto',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                >
                  <ArrowForward />
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}
      <ScrollToTopButton />
    </Container>
  );
};

export default ProductDetail;
